import '../styles/Training.css';
import { Link } from 'react-router-dom';

function Training() {
  const belts = [
    {
      id: 'yellow-belt',
      color: '#D4A017',
      colorLight: 'rgba(212, 160, 23, 0.12)',
      colorBorder: 'rgba(212, 160, 23, 0.4)',
      label: 'Yellow Belt',
      emoji: '🟡',
      tagline: 'Introductory Lean Six Sigma Certification',
      duration: '1–2 Days',
      audience: 'Those new to Lean Six Sigma, typically in a position of support within a project team, or those looking to build foundational problem-solving and process improvement skills.',
      description: 'Build your skills in problem solving, process improvement and operational efficiency with the Lean Six Sigma Yellow Belt. This introductory certification gives you a comprehensive introduction to process improvement, using Lean Six Sigma\'s data-driven approach to reducing defects and enhancing quality. Learn about the history of Lean Six Sigma, its basic concepts and an overview of the DMAIC methodology — equipping you to become a valued member of any project team.',
      outcomes: [
        'Understand the history and core principles of Lean Six Sigma',
        'Apply Lean Six Sigma\'s data-driven approach to reducing defects',
        'Navigate an overview of the DMAIC methodology (Define, Measure, Analyse, Improve and Control)',
        'Identify waste and inefficiency within processes using Lean thinking',
        'Contribute effectively as a support member within a project team',
        'Communicate process improvement concepts with confidence',
        'Build the foundation to progress towards Green Belt certification'
      ],
      modules: [
        { title: 'Introduction to Six Sigma', desc: 'History, philosophy, and the business case for quality improvement' },
        { title: 'The Six Sigma Roles', desc: 'Belt levels, responsibilities, and how teams are structured' },
        { title: 'Understanding Basic Process', desc: 'Process mapping, inputs, outputs, and identifying waste' },
        { title: 'Introduction to DMAIC', desc: 'Overview of the Define, Measure, Analyse, Improve and Control methodology' },
        { title: 'Basic Lean Concepts', desc: 'Lean thinking introduction, continuous improvement mindset, and value stream basics' }
      ]
    },
    {
      id: 'green-belt',
      color: '#27AE60',
      colorLight: 'rgba(39, 174, 96, 0.12)',
      colorBorder: 'rgba(39, 174, 96, 0.4)',
      label: 'Green Belt',
      emoji: '🟢',
      tagline: 'Project-Led Improvement Practitioner',
      duration: '4–5 Days',
      audience: 'Team leaders, managers, analysts, and professionals who want to lead improvement projects and drive measurable change in their organisation.',
      description: 'The Green Belt programme is a comprehensive, project-led qualification that equips participants to independently lead Lean Six Sigma improvement projects. You will develop practical skills across all five phases of DMAIC, from problem definition through to sustained control — using real data and proven tools.',
      outcomes: [
        'Lead end-to-end Lean Six Sigma improvement projects using DMAIC',
        'Define project scope with a robust Project Charter and SIPOC',
        'Collect and analyse data to identify root causes of process variation',
        'Apply statistical tools including hypothesis testing and capability analysis',
        'Design and implement effective, data-driven solutions',
        'Create control plans to sustain improvements long-term',
        'Coach and mentor Yellow Belt team members'
      ],
      modules: [
        { title: 'Define Phase', desc: 'Project Charter, VOC, CTQ, SIPOC, stakeholder mapping' },
        { title: 'Measure Phase', desc: 'Data collection, measurement system analysis, process capability' },
        { title: 'Analyse Phase', desc: 'Root cause analysis, fishbone, 5 Whys, hypothesis testing' },
        { title: 'Improve Phase', desc: 'Solution generation, piloting, risk assessment (FMEA)' },
        { title: 'Control Phase', desc: 'Control charts, control plans, sustaining improvements' },
        { title: 'Statistical Tools', desc: 'Minitab / Excel-based analysis, regression' }
      ]
    }
  ];

  const whyUs = [
    {
      icon: (
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
          <path d="M14 3L17.5 10.5H25L19 15.5L21.5 23L14 18.5L6.5 23L9 15.5L3 10.5H10.5L14 3Z" fill="currentColor"/>
        </svg>
      ),
      title: 'Practitioner-Led Training',
      desc: 'Delivered by experienced Lean Six Sigma practitioners — not just trainers. Real-world examples from diverse industries.'
    },
    {
      icon: (
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
          <rect x="3" y="6" width="22" height="16" rx="2" stroke="currentColor" strokeWidth="2"/>
          <path d="M9 12L12 15L19 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      title: 'Accredited Programme',
      desc: 'Our training is aligned to internationally recognised Lean Six Sigma standards, delivered to a Chartered Management Consultancy benchmark.'
    },
    {
      icon: (
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
          <circle cx="14" cy="10" r="5" stroke="currentColor" strokeWidth="2"/>
          <path d="M6 24C6 20.134 9.582 17 14 17C18.418 17 22 20.134 22 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      ),
      title: 'Tailored to Your Context',
      desc: 'Training can be tailored to your sector, team size, and current capability — from healthcare and manufacturing to services and construction.'
    },
    {
      icon: (
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
          <path d="M5 14H23M14 5V23" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <circle cx="14" cy="14" r="11" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      title: 'Post-Training Support',
      desc: 'Participants receive coaching support after training to help apply skills in live projects, ensuring real-world impact beyond the classroom.'
    }
  ];

  return (
    <div className="training">
      {/* Hero */}
      <section className="training-hero">
        <div className="training-hero-bg">
          <div className="training-hero-overlay"></div>
        </div>
        <div className="training-hero-content">
          <span className="training-hero-label">LEAN SIX SIGMA TRAINING</span>
          <h1 className="training-hero-heading">
            Build Capability.<br />Drive Real Change.
          </h1>
          <p className="training-hero-sub">
            Accredited Lean Six Sigma training programmes for individuals and teams — from Yellow Belt awareness through to Green Belt project-led improvement.
          </p>
          <div className="training-hero-ctas">
            <Link to="/contact" className="training-cta-primary">Enquire About Training</Link>
            <a href="#belt-programmes" className="training-cta-secondary">View Programmes ↓</a>
          </div>
        </div>
      </section>

      {/* Why Train With Us */}
      <section className="training-why">
        <div className="training-why-container">
          <div className="training-why-header">
            <span className="section-label-tag">WHY TRAIN WITH US</span>
            <h2 className="training-section-heading">Expert-led. Practical. Results-focused.</h2>
            <p className="training-section-sub">
              CKS Innovation delivers Lean Six Sigma training that goes beyond theory — grounded in real consulting experience and built to create lasting capability within your organisation.
            </p>
          </div>
          <div className="training-why-grid">
            {whyUs.map((item, i) => (
              <div key={i} className="training-why-card">
                <div className="training-why-icon">{item.icon}</div>
                <h4 className="training-why-title">{item.title}</h4>
                <p className="training-why-desc">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Belt Programmes */}
      <section className="training-belts" id="belt-programmes">
        <div className="training-belts-container">
          <div className="training-belts-header">
            <span className="section-label-tag">OUR PROGRAMMES</span>
            <h2 className="training-section-heading">Training Up to Green Belt Level</h2>
            <p className="training-section-sub">
              We offer structured Lean Six Sigma training from foundational awareness through to full project leadership — helping individuals and teams build the right level of capability for their role.
            </p>
          </div>

          <div className="training-belt-progression">
            <div className="progression-track">
              <div className="progression-step active">
                <div className="prog-dot" style={{ background: '#D4A017' }}></div>
                <span>Yellow Belt</span>
              </div>
              <div className="progression-line"></div>
              <div className="progression-step active">
                <div className="prog-dot" style={{ background: '#27AE60' }}></div>
                <span>Green Belt</span>
              </div>
              <div className="progression-line dimmed"></div>
              <div className="progression-step dimmed">
                <div className="prog-dot dimmed-dot"></div>
                <span>Black Belt</span>
              </div>
            </div>
            <p className="progression-note">CKS Innovation delivers training up to Green Belt level</p>
          </div>

          {belts.map((belt, idx) => (
            <div key={belt.id} className={`belt-card ${idx % 2 === 1 ? 'belt-card-reverse' : ''}`} style={{ '--belt-color': belt.color, '--belt-light': belt.colorLight, '--belt-border': belt.colorBorder }}>
              <div className="belt-card-header">
                <div className="belt-badge" style={{ background: belt.colorLight, border: `2px solid ${belt.colorBorder}` }}>
                  <span className="belt-emoji">{belt.emoji}</span>
                  <div>
                    <div className="belt-name" style={{ color: belt.color }}>{belt.label}</div>
                    <div className="belt-tagline">{belt.tagline}</div>
                  </div>
                </div>
                <div className="belt-meta">
                  <div className="belt-meta-item">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/>
                      <path d="M8 4V8L11 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                    <span>{belt.duration}</span>
                  </div>
                </div>
              </div>

              <div className="belt-card-body">
                <div className="belt-left">
                  <p className="belt-audience-label">Who is this for?</p>
                  <p className="belt-audience">{belt.audience}</p>

                  <p className="belt-desc-label">About the Programme</p>
                  <p className="belt-description">{belt.description}</p>

                  <div className="belt-modules">
                    <p className="belt-modules-label">Programme Modules</p>
                    <div className="belt-modules-list">
                      {belt.modules.map((mod, i) => (
                        <div key={i} className="belt-module-item">
                          <div className="belt-module-dot" style={{ background: belt.color }}></div>
                          <div>
                            <strong>{mod.title}</strong>
                            <span> — {mod.desc}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="belt-right">
                  <div className="belt-outcomes-box" style={{ background: belt.colorLight, borderLeft: `4px solid ${belt.color}` }}>
                    <p className="belt-outcomes-title" style={{ color: belt.color }}>
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={{ verticalAlign: 'middle', marginRight: '6px' }}>
                        <path d="M9 2L11 7H16L12 10.5L13.5 15.5L9 12.5L4.5 15.5L6 10.5L2 7H7L9 2Z" fill="currentColor"/>
                      </svg>
                      What You'll Achieve
                    </p>
                    <ul className="belt-outcomes-list">
                      {belt.outcomes.map((outcome, i) => (
                        <li key={i} className="belt-outcome-item">
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ minWidth: '14px', marginTop: '2px' }}>
                            <circle cx="7" cy="7" r="6" fill={belt.color} opacity="0.15"/>
                            <path d="M4 7L6 9L10 5" stroke={belt.color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          {outcome}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Link to="/contact" className="belt-enquire-btn" style={{ background: belt.color }}>
                    Enquire About {belt.label}
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Delivery Options */}
      <section className="training-delivery">
        <div className="training-delivery-container">
          <span className="section-label-tag">HOW WE DELIVER</span>
          <h2 className="training-section-heading">Flexible Delivery Options</h2>
          <div className="training-delivery-grid">
            <div className="delivery-card">
              <div className="delivery-icon">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <rect x="4" y="8" width="24" height="16" rx="2" stroke="currentColor" strokeWidth="2"/>
                  <path d="M16 8V4M10 8V6M22 8V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M9 16H23M9 20H17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
              <h4>On-Site Training</h4>
              <p>Delivered at your premises — ideal for team-based programmes where group learning creates shared language and momentum.</p>
            </div>
            <div className="delivery-card">
              <div className="delivery-icon">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <rect x="3" y="5" width="26" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                  <path d="M10 23L8 27M22 23L24 27M8 27H24" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <circle cx="16" cy="14" r="4" stroke="currentColor" strokeWidth="1.5"/>
                </svg>
              </div>
              <h4>Virtual / Online</h4>
              <p>Live virtual sessions with full interaction — no travel required. Ideal for remote teams or multi-site organisations.</p>
            </div>
            <div className="delivery-card">
              <div className="delivery-icon">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <path d="M16 4C10 4 5 9 5 15C5 21 10 26 16 26C22 26 27 21 27 15C27 9 22 4 16 4Z" stroke="currentColor" strokeWidth="2"/>
                  <path d="M16 10V15L19 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <h4>Blended Programme</h4>
              <p>A combination of self-paced learning materials and live facilitated sessions — giving flexibility without losing interaction.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="training-cta-section">
        <div className="training-cta-container">
          <h2>Ready to Build Lean Six Sigma Capability?</h2>
          <p>Speak to us about training programmes tailored to your team, sector, and goals.</p>
          <div className="training-cta-buttons">
            <Link to="/contact" className="training-cta-primary">Book a Training Discussion</Link>
            <Link to="/services" className="training-cta-secondary-outline">View All Services</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Training;
